import React, { useContext, useState, useMemo, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormButton } from 'components/buttons/formButton';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { getTranslatedData } from 'locales';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { renderTime } from 'utils/dayTimeHelpers';
import { updateRestrictedResources } from 'api/restaurant';
import { files } from 'codemash';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { TranslationFieldOptions, getValidationShapeForTranslatableText } from 'utils/schemaHelper';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { apiActionChooser } from 'utils/apiActionChooser';
import { postFile } from 'api/client/requests';
import { defaultLanguageCodes } from './general';

const orderInFutureTimes = [
  { value: 'Only_Today', label: 'Only Today' },
  { value: '1_Day', label: '1 Day' },
  { value: '2_Days', label: '2 Days' },
  { value: '3_Days', label: '3 Days' },
  { value: '4_Days', label: '4 Days' },
  { value: '5_Days', label: '5 Days' },
  { value: '6_Days', label: '6 Days' },
];

export const RestaurantsDeliverySingle = ({ itemData }) => {
  const navigate = useNavigate();

  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);
  const { editById } = useContext(CompanyContext);

  const [loading, setLoading] = useState(false);

  const [bannerImage, setBannerImage] = useState([]);
  const [image, setImage] = useState([]);

  const convertMinutesToTimeString = (mins) => {
    if (mins === null || mins === undefined) {
      return '';
    }

    const hours = Math.trunc(mins / 60);
    const minutes = mins % 60;

    return renderTime(hours, minutes);
  };

  const convertTimeStringToMinutes = (timeString) => {
    if (!timeString) {
      return undefined;
    }

    const timeParts = timeString.split(':');

    if (!timeParts || timeParts.length !== 2) {
      return undefined;
    }

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return undefined;
    }

    return hours * 60 + minutes;
  };

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    disable_orders: yup.boolean().default(itemData?.disable_orders || false),
    display_events: yup.boolean().default(itemData?.display_events || false),
    display_promotions: yup.boolean().default(itemData?.display_promotions || false),

    delivery_now_min: yup.number()
      .nullable(true)
      // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.delivery_now_min),
    delivery_now_max: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.delivery_now_max),
    takeaway_now_min: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.takeaway_now_min),
    takeaway_now_max: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.takeaway_now_max),

    delivery_price: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.delivery_price),
    delivery_price_percent: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.delivery_price_percent),
    min_delivery_order: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_delivery_order),
    min_takeaway_order: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.min_takeaway_order),
    delivery_order_until_closure: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.delivery_order_until_closure),
    takeaway_order_until_closure: yup.number()
      .nullable(true)
    // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.takeaway_order_until_closure),

    mon_from: yup.string().default(convertMinutesToTimeString(itemData?.mon_from)),
    mon_to: yup.string().default(convertMinutesToTimeString(itemData?.mon_to)),
    tue_from: yup.string().default(convertMinutesToTimeString(itemData?.tue_from)),
    tue_to: yup.string().default(convertMinutesToTimeString(itemData?.tue_to)),
    wed_from: yup.string().default(convertMinutesToTimeString(itemData?.wed_from)),
    wed_to: yup.string().default(convertMinutesToTimeString(itemData?.wed_to)),
    thu_from: yup.string().default(convertMinutesToTimeString(itemData?.thu_from)),
    thu_to: yup.string().default(convertMinutesToTimeString(itemData?.thu_to)),
    fri_from: yup.string().default(convertMinutesToTimeString(itemData?.fri_from)),
    fri_to: yup.string().default(convertMinutesToTimeString(itemData?.fri_to)),
    sat_from: yup.string().default(convertMinutesToTimeString(itemData?.sat_from)),
    sat_to: yup.string().default(convertMinutesToTimeString(itemData?.sat_to)),
    sun_from: yup.string().default(convertMinutesToTimeString(itemData?.sun_from)),
    sun_to: yup.string().default(convertMinutesToTimeString(itemData?.sun_to)),

    phone: yup.string().nullable().default(itemData?.phone),
    email: yup.string().nullable().default(itemData?.email),
    order_receive_phone: yup.string().nullable().default(itemData?.order_receive_phone),
    order_driver_phone: yup.string().nullable().default(itemData?.order_driver_phone),
    restaurant_language: yup.string().nullable().default(itemData?.restaurant_language),
    max_order_in_future: yup.string().default(itemData?.max_order_in_future || '6_Days'),

    description: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.description,
          TranslationFieldOptions.OPTIONAL,
        ),
      ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData?.banner) {
      setBannerImage([{ data_url: formFileUrlFromFileArray(itemData?.banner, true) }]);
    }

    if (itemData?.image) {
      setImage([{ data_url: formFileUrlFromFileArray(itemData?.image, true) }]);
    }
  }, [itemData]);

  const onSubmit = async (data) => {
    setLoading(true);

    let bannerImageId;
    let imageId;
    let deleteBannerImage = false;
    let deleteImage = false;

    if (bannerImage && bannerImage.length > 0) {
      if (bannerImage[0].file !== null && bannerImage[0].file !== undefined) {
        await apiActionChooser(auth.company?.company, async () => {
          const bannerImageResponse = await files.uploadFile({
            secretKey: auth.token,
            file: bannerImage[0].file,
          });

          bannerImageId = bannerImageResponse.key;
        }, async () => {
          const fileResp = await postFile({
            fieldPath: 'banner',
            file: bannerImage[0].file,
            resource: 'DeliveryRestaurantImages',
          });

          bannerImageId = fileResp.id;
        });
      }
    } else {
      deleteBannerImage = true;
    }

    if (image && image.length > 0) {
      if (image[0].file !== null && image[0].file !== undefined) {
        await apiActionChooser(auth.company?.company, async () => {
          const imageResponse = await files.uploadFile({
            secretKey: auth.token,
            file: image[0].file,
          });

          imageId = imageResponse.key;
        }, async () => {
          const fileResp = await postFile({
            fieldPath: 'image',
            file: bannerImage[0].file,
            resource: 'DeliveryRestaurantImages',
          });

          imageId = fileResp.id;
        });
      }
    } else {
      deleteImage = true;
    }

    const sumbitData = {
      _id: data._id,
      disable_orders: data.disable_orders,
      display_promotions: data.display_promotions,
      display_events: data.display_events,
      delivery_now_min: data.delivery_now_min,
      delivery_now_max: data.delivery_now_max,
      takeaway_now_min: data.takeaway_now_min,
      takeaway_now_max: data.takeaway_now_max,
      delivery_price: data.delivery_price,
      delivery_price_percent: data.delivery_price_percent,
      min_delivery_order: data.min_delivery_order,
      min_takeaway_order: data.min_takeaway_order,
      mon_from: data.mon_from,
      mon_to: data.mon_to,
      tue_from: data.tue_from,
      tue_to: data.tue_to,
      wed_from: data.wed_from,
      wed_to: data.wed_to,
      thu_from: data.thu_from,
      thu_to: data.thu_to,
      fri_from: data.fri_from,
      fri_to: data.fri_to,
      sat_from: data.sat_from,
      sat_to: data.sat_to,
      sun_from: data.sun_from,
      sun_to: data.sun_to,
      phone: data.phone,
      email: data.email,
      order_receive_phone: data.order_receive_phone,
      order_driver_phone: data.order_driver_phone,
      description: data.description,
      restaurant_language: data.restaurant_language,
      max_order_in_future: data.max_order_in_future,
      delivery_order_until_closure: data.delivery_order_until_closure,
      takeaway_order_until_closure: data.takeaway_order_until_closure,

      bannerImageId,
      imageId,
      deleteBannerImage,
      deleteImage,
    };

    await apiActionChooser(auth.company?.company, async () => {
      const response = await updateRestrictedResources({
        token: auth.token,
        updateKey: 'delivery_restaurant',
        values: sumbitData,
        alertController,
      });

      if (response && response.isAcknowledged) {
        alertController.success({ text: 'Delivery settings updated' });
      }
    }, async () => {
      const sumbitDataV2 = {
        _id: data._id,
        disable_orders: data.disable_orders,
        display_promotions: data.display_promotions,
        display_events: data.display_events,
        delivery_now_min: data.delivery_now_min,
        delivery_now_max: data.delivery_now_max,
        takeaway_now_min: data.takeaway_now_min,
        takeaway_now_max: data.takeaway_now_max,
        delivery_price: data.delivery_price,
        delivery_price_percent: data.delivery_price_percent,
        min_delivery_order: data.min_delivery_order,
        min_takeaway_order: data.min_takeaway_order,
        schedule: [{
          mon_from: convertTimeStringToMinutes(data.mon_from),
          mon_to: convertTimeStringToMinutes(data.mon_to),
          tue_from: convertTimeStringToMinutes(data.tue_from),
          tue_to: convertTimeStringToMinutes(data.tue_to),
          wed_from: convertTimeStringToMinutes(data.wed_from),
          wed_to: convertTimeStringToMinutes(data.wed_to),
          thu_from: convertTimeStringToMinutes(data.thu_from),
          thu_to: convertTimeStringToMinutes(data.thu_to),
          fri_from: convertTimeStringToMinutes(data.fri_from),
          fri_to: convertTimeStringToMinutes(data.fri_to),
          sat_from: convertTimeStringToMinutes(data.sat_from),
          sat_to: convertTimeStringToMinutes(data.sat_to),
          sun_from: convertTimeStringToMinutes(data.sun_from),
          sun_to: convertTimeStringToMinutes(data.sun_to),
        }],
        phone: data.phone,
        email: data.email,
        order_receive_phone: data.order_receive_phone,
        order_driver_phone: data.order_driver_phone,
        description: data.description,
        restaurant_language: data.restaurant_language,
        max_order_in_future: data.max_order_in_future,
        delivery_order_until_closure: data.delivery_order_until_closure,
        takeaway_order_until_closure: data.takeaway_order_until_closure,
        banner: bannerImageId,
        image: imageId,
        deleteBannerImage,
        deleteImage,
      };

      await editById(
        {
          id: data._id,
          resourceV2: 'AdminDeliveryRestaurant',
          updateTypeV2: 'Delivery',
          updateDataV2: sumbitDataV2,
          companyIdForV2: auth.company?.company,
        },
        async () => {
          alertController.success({ text: 'Delivery settings updated' });
        },
      );
    });

    setLoading(false);
  };

  const onError = (data) => console.log(data);

  const renderWorkDayHours = (dayName, fromField, toField) => {
    return (
      <div className="col-span-6 flex flex-row items-center flex-wrap sm:flex-nowrap">
        <div className="w-[100%] md:w-[50%] lg:w-[50%] xl:w-[30%]">
          <label className="block text-sm font-medium text-gray-700">
            {dayName}
          </label>
        </div>

        <div className="flex flex-row items-center">
          <TextboxWrapper
            register={register}
            name={fromField}
            error={errors ? errors[fromField] : null}
            responsiveClassName="col-span-6 sm:col-span-3"
            placeholder="HH:mm"
          />

          <div className="w-6 text-center">-</div>

          <TextboxWrapper
            register={register}
            name={toField}
            error={errors ? errors[toField] : null}
            responsiveClassName="col-span-6 sm:col-span-3"
            placeholder="HH:mm"
          />
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {getTranslatedData(itemData.title)}
            </h3>
          </div>

          <div className="grid grid-cols-6 gap-6">

            <div className="col-span-6 w-full h-[1px] bg-gray-100" />

            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Banner</label>
              <ImageUpload
                id="bannerImage"
                name="bannerImage"
                images={bannerImage}
                onChange={(val) => setBannerImage(val)}
              />
            </div>
            {itemData.total_restaurants > 1 && (
            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Selection Image</label>
              <ImageUpload
                id="image"
                name="image"
                images={image}
                onChange={(val) => setImage(val)}
              />
            </div>
            )}

            <div className="sm:col-span-4 col-span-6">
              <TranslatableInput
                name="description"
                register={register}
                error={errors?.description}
                containerClassName="col-span-6 sm:col-span-4"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Description"
                renderAsTextArea
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Work hours (delivery / takeaway)</p>
            </div>

            <div className="col-span-6 grid grid-cols-6 gap-4 sm:gap-2">
              {renderWorkDayHours('Monday', 'mon_from', 'mon_to')}
              {renderWorkDayHours('Tuesday', 'tue_from', 'tue_to')}
              {renderWorkDayHours('Wednesday', 'wed_from', 'wed_to')}
              {renderWorkDayHours('Thursday', 'thu_from', 'thu_to')}
              {renderWorkDayHours('Friday', 'fri_from', 'fri_to')}
              {renderWorkDayHours('Saturday', 'sat_from', 'sat_to')}
              {renderWorkDayHours('Sunday', 'sun_from', 'sun_to')}
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Order times</p>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="takeaway_now_min"
                title="Prepare from"
                subtitle="Minimum time required to prepare an order"
                error={errors?.takeaway_now_min}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="takeaway_now_max"
                title="Prepare until"
                subtitle="Maximum time required to prepare an order"
                error={errors?.takeaway_now_max}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            {itemData.has_delivery && (
            <>
              <div className="col-span-6 sm:col-span-3">
                <TextboxWrapper
                  register={register}
                  name="delivery_now_min"
                  title="Delivery from"
                  subtitle="Minimum time required for preparation + delivery"
                  error={errors?.delivery_now_min}
                  responsiveClassName="col-span-6 sm:col-span-3"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <TextboxWrapper
                  register={register}
                  name="delivery_now_max"
                  title="Delivery until"
                  subtitle="Maximum time required for preparation + delivery"
                  error={errors?.delivery_now_max}
                  responsiveClassName="col-span-6 sm:col-span-3"
                />
              </div>
            </>
            )}

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="takeaway_order_until_closure"
                title="Takeaway until closure"
                subtitle="Prevent takeaway orders if less minutes left until closure"
                error={errors?.takeaway_order_until_closure}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="delivery_order_until_closure"
                title="Delivery until closure"
                subtitle="Prevent delivery orders if less minutes left until closure"
                error={errors?.delivery_order_until_closure}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <DropdownWrapper
                register={register}
                name="max_order_in_future"
                title="Order in advance"
                subtitle="How many days can orders be placed in advance"
                options={orderInFutureTimes}
                error={errors?.max_order_in_future}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Pricing</p>
            </div>

            {itemData.has_takeaway && (
            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="min_takeaway_order"
                title="Minimum takeaway total"
                subtitle="Minimum order amount for takeaway"
                error={errors?.min_takeaway_order}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>
            )}

            {itemData.has_delivery && (
            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="min_delivery_order"
                title="Minimum delivery total"
                subtitle="Minimum order amount for delivery"
                error={errors?.min_delivery_order}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>
            )}

            {itemData.has_delivery && (
            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="delivery_price"
                title="Delivery price"
                subtitle="Static fee for delivery to client (higher priority)"
                error={errors?.delivery_price}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>
            )}

            {itemData.has_delivery && (
            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="delivery_price_percent"
                title="Delivery price percent"
                subtitle="Dynamic fee for delivery to client (lower priority)"
                error={errors?.delivery_price_percent}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>
            )}

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Contact</p>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="phone"
                title="Pubic phone"
                subtitle="Public phone number for clients"
                error={errors?.phone}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="email"
                title="Public email"
                subtitle="Public email address for clients"
                error={errors?.email}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="order_receive_phone"
                title="Order updates phone"
                subtitle="Private phone number to receive order updates"
                error={errors?.order_receive_phone}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <TextboxWrapper
                register={register}
                name="order_driver_phone"
                title="Driver phone"
                subtitle="Private phone number for driver"
                error={errors?.order_driver_phone}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <DropdownWrapper
                register={register}
                name="restaurant_language"
                title="Restaurant internal language"
                subtitle="Language for notifications for the restaurant"
                options={defaultLanguageCodes}
                error={errors?.restaurant_language}
                responsiveClassName="col-span-6 sm:col-span-3"
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Additional</p>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="display_events"
                title="Display events"
                subtitle="Displays events menu option"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="display_promotions"
                title="Display promotions"
                subtitle="Displays promotions menu option"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <CheckboxWrapper
                register={register}
                name="disable_orders"
                title="Disable Orders"
                subtitle="Clients won't be able to make new orders while this is checked"
                responsiveClassName="w-full"
                isDanger
              />
            </div>

          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <button
            type="button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => navigate('/settings/restaurants')}
          >
            Cancel
          </button>
          <FormButton text="Save" isLoading={loading} />
        </div>
      </div>
    </form>
  );
};
